// CSC Load-Profile analysis — the user's TNB 30-min interval method (2026-07-24).
// Upload a TNB interval Excel → parse import kWh per 30-min slot → size the system
// (P25 of daily daytime kWh ÷ PSH) → distribute a Gaussian solar day (σ=2.2, peak 12:30
// over 08:00–18:00) → per-slot export/self-consumption → stats + Daily/Hourly/Monthly
// aggregations + anomaly flags. Pure functions (browser + node testable).
//
// Method constants (user-validated against the Cheluan Khor profile):
export const PSH = 3.4;               // peak sun hours / day (Malaysia standard)
export const DAY_START = 8, DAY_END = 18;   // daytime window (also the solar window)
export const GAUSS_PEAK = 12.5;       // solar noon
export const GAUSS_SIGMA = 2.2;
const SLOTS_PER_DAY = 48;             // 30-min
const SLOT_H = 0.5;

// ── Excel serial (days since 1899-12-30) → JS Date ──────────────────────────
export function excelSerialToDate(v) {
    if (v instanceof Date) return v;
    if (typeof v === 'number' && v > 20000 && v < 80000) {
        return new Date(Math.round((v - 25569) * 86400000)); // 25569 = 1970-01-01 serial
    }
    if (typeof v === 'string') { const d = new Date(v); if (!isNaN(d)) return d; }
    return null;
}

// ── Parse the sheet rows (array-of-arrays) → [{ serial, kwImport }] ──────────
// Header row is the first row containing a "kw" + "import" cell. We keep the raw
// Excel serial (days, local time, NO timezone) — time-of-day = the fractional part.
// This avoids the UTC±offset trap of converting to a JS Date and reading getHours().
export function parseTnbRows(rows) {
    let hdr = -1, tCol = 0, impCol = -1;
    for (let i = 0; i < Math.min(rows.length, 15); i++) {
        const cells = (rows[i] || []).map(c => String(c ?? '').toLowerCase());
        const k = cells.findIndex(c => c.includes('kw') && c.includes('import'));
        if (k >= 0) {
            hdr = i; impCol = k;
            const d = cells.findIndex(c => c.includes('date') || c.includes('time'));
            tCol = d >= 0 ? d : 0;
            break;
        }
    }
    if (hdr < 0) return [];
    const recs = [];
    for (let i = hdr + 1; i < rows.length; i++) {
        const r = rows[i]; if (!r) continue;
        const raw = r[tCol];
        let serial = null;
        if (typeof raw === 'number') serial = raw;
        else { const d = excelSerialToDate(raw); if (d) serial = d.getTime() / 86400000 + 25569; }
        const kw = parseFloat(r[impCol]);
        if (serial != null && !isNaN(kw)) recs.push({ serial, kwImport: kw });
    }
    return recs;
}

// ── Gaussian solar shape over the day → 48-slot weights summing to 1 (0 outside 8–18) ──
function solarWeights() {
    const w = new Array(SLOTS_PER_DAY).fill(0);
    let sum = 0;
    for (let s = 0; s < SLOTS_PER_DAY; s++) {
        const hr = s * SLOT_H + SLOT_H / 2;             // slot mid-hour
        if (hr < DAY_START || hr >= DAY_END) continue;
        const g = Math.exp(-((hr - GAUSS_PEAK) ** 2) / (2 * GAUSS_SIGMA ** 2));
        w[s] = g; sum += g;
    }
    if (sum > 0) for (let s = 0; s < SLOTS_PER_DAY; s++) w[s] /= sum;
    return w;
}

const median = (arr) => {
    if (!arr.length) return 0;
    const a = [...arr].sort((x, y) => x - y);
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};
const mean = (arr) => arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0;
const percentile = (arr, p) => {
    if (!arr.length) return 0;
    const a = [...arr].sort((x, y) => x - y);
    const idx = Math.min(a.length - 1, Math.max(0, Math.round((p / 100) * (a.length - 1))));
    return a[idx];
};
const stdev = (arr) => {
    if (arr.length < 2) return 0;
    const m = mean(arr);
    return Math.sqrt(arr.reduce((s, x) => s + (x - m) ** 2, 0) / arr.length);
};
const isDaySlot = (s) => (s * SLOT_H) >= DAY_START && (s * SLOT_H) < DAY_END;

// ── Full analysis. opts.kwp overrides the P25-derived sizing. ───────────────
export function analyzeLoadProfile(records, opts = {}) {
    if (!records || !records.length) return null;

    // Bucket import kWh by calendar day + 30-min slot, straight off the Excel serial.
    // TNB stamps the END of each interval → a read at HH:30 covers HH:00–HH:30, so we
    // nudge back one instant (−1e-9 day) before flooring: the 30-min slot = that interval,
    // and a 00:00 read rolls into the previous day's 23:30 slot.
    const days = new Map();   // dayKey (int serial) → { slots: Float[48], serial }
    for (const r of records) {
        const adj = r.serial - 1e-9;
        const dayKey = Math.floor(adj);
        const slot = Math.min(SLOTS_PER_DAY - 1, Math.max(0, Math.floor((adj - dayKey) * SLOTS_PER_DAY)));
        if (!days.has(dayKey)) days.set(dayKey, { slots: new Array(SLOTS_PER_DAY).fill(0), serial: dayKey, seen: new Set() });
        days.get(dayKey).slots[slot] += r.kwImport * SLOT_H;   // kWh this 30-min
        days.get(dayKey).seen.add(slot);   // coverage, to spot a part-day at either end
    }

    /* DROP THE PART-DAYS AT THE ENDS. A TNB export usually starts and finishes mid-day,
       so the first or last entry can be a stub — the Penang Sports Club file ends with
       10-31 carrying ONE of 48 half-hour readings, 49 kWh of import.
       Every day is credited a full day of solar (kWp x PSH), which is right for a real
       day and nonsense for a stub: 813 kWh of generation lands against an almost empty
       load, nearly all of it counted as export, and the daily chart ends in a spike that
       is an artefact of the export window rather than anything the site did. It also
       drags the "lowest" figure on the import cards down to a number no day truly hit.
       A full day is 48 slots; 44 allows a meter to miss one or two without being thrown
       away. (user 2026-11-04: "last day the generation is there but no data") */
    const MIN_SLOTS_FOR_A_FULL_DAY = 44;
    const allDays = [...days.values()].sort((a, b) => a.serial - b.serial);
    const fullDays = allDays.filter(d => d.seen.size >= MIN_SLOTS_FOR_A_FULL_DAY);
    const partialDays = allDays.filter(d => d.seen.size < MIN_SLOTS_FOR_A_FULL_DAY);
    // Only drop them when something is left to analyse — a short upload that is ALL
    // part-days is better shown as it is than reduced to nothing.
    const dayList = fullDays.length ? fullDays : allDays;
    // Calendar-date label from the day serial (UTC read → correct MM-DD, no tz shift).
    const dayLabel = (serial) => { const d = excelSerialToDate(serial); return d ? `${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` : ''; };
    dayList.forEach(d => { d.label = dayLabel(d.serial); });

    // Daily day/night import totals.
    const dayImp = [], nightImp = [];
    for (const d of dayList) {
        let di = 0, ni = 0;
        d.slots.forEach((kwh, s) => { if (isDaySlot(s)) di += kwh; else ni += kwh; });
        d.dayImport = di; d.nightImport = ni;
        dayImp.push(di); nightImp.push(ni);
    }

    // Sizing: P25 of daily daytime kWh ÷ PSH, round to nearest 5 (unless kwp given).
    const p25Day = percentile(dayImp, 25);
    const kwp = opts.kwp != null && opts.kwp > 0
        ? Number(opts.kwp)
        : Math.max(5, Math.round((p25Day / PSH) / 5) * 5);
    const dailySolar = kwp * PSH;
    const weights = solarWeights();
    const solarSlot = weights.map(w => w * dailySolar);

    // Per-day, per-slot export / self-consumption.
    const exportDay = [];
    let totalSelf = 0, totalExport = 0, totalSolar = 0;
    for (const d of dayList) {
        let exp = 0, self = 0;
        for (let s = 0; s < SLOTS_PER_DAY; s++) {
            const sol = solarSlot[s]; if (sol <= 0) continue;
            const e = Math.max(0, sol - d.slots[s]);
            exp += e; self += sol - e;
        }
        d.export = exp; d.self = self;
        exportDay.push(exp);
        totalExport += exp; totalSelf += self; totalSolar += dailySolar;
    }

    const nDays = dayList.length || 1;
    const stats = {
        kwp, dailySolar, nDays,
        dayImportMedian: median(dayImp), dayImportMean: mean(dayImp),
        nightImportMedian: median(nightImp), nightImportMean: mean(nightImp),
        solarPerDay: dailySolar,
        exportMedian: median(exportDay), exportMean: mean(exportDay), exportMax: Math.max(0, ...exportDay),
        selfPerDay: totalSelf / nDays,
        p25Day,
        // Maximum demand = the highest kW reading in the profile (drives ATAP export cap).
        mdKw: records.reduce((mx, r) => Math.max(mx, r.kwImport), 0),
        // Monthly usage estimate = mean daily total × ~30 (for the CSC Monthly Usage field).
        monthlyKwh: (mean(dayImp) + mean(nightImp)) * 30,
        /* EVERY CALENDAR MONTH THE FILE ACTUALLY CONTAINS, measured rather than averaged.
           A 61-day export holds TWO real months, and monthlyKwh above flattens them into
           one mean - which is the only month-to-month evidence the upload carries. On the
           user's own file that was March 256,519 kWh over 31 days against April 306,520
           over 30, April running 23.5% higher, all of it collapsed to ~276,958.
           (user 2026-10-26: "from load profile is jst 2months, how u noe the 6months
           cycle or?")

           `days` is reported so the caller can tell a COMPLETE month from the stub at
           either end of the range - a 3-day fragment of May is not a month and must not
           become one. The caller decides the threshold; this only measures. */
        calendarMonths: (() => {
            const by = new Map();
            for (const d of dayList) {
                const dt = excelSerialToDate(d.serial);
                if (!dt) continue;
                const key = dt.getUTCFullYear() * 12 + dt.getUTCMonth();
                const cur = by.get(key)
                    || { year: dt.getUTCFullYear(), month: dt.getUTCMonth(), days: 0, kwh: 0 };
                cur.days += 1;
                cur.kwh += d.dayImport + d.nightImport;
                by.set(key, cur);
            }
            return [...by.values()].sort((a, b) => (a.year - b.year) || (a.month - b.month));
        })(),
    };

    // Anomaly flags.
    const dMed = stats.dayImportMedian;
    const hourlyAvg = new Array(24).fill(0), hourlyCnt = new Array(24).fill(0);
    for (const d of dayList) d.slots.forEach((kwh, s) => { const h = Math.floor(s / 2); hourlyAvg[h] += kwh; hourlyCnt[h]++; });
    const hAvg = hourlyAvg.map((v, h) => hourlyCnt[h] ? v / hourlyCnt[h] : 0);
    /* kWh IN THAT HOUR OF A TYPICAL DAY. `hAvg` above divides by hourlyCnt, which counts
       one per 30-MINUTE SLOT — two per hour per day — so it is the mean per half hour.
       That is the right quantity for the flat-24/7 flag below (a stdev/mean ratio does
       not care about scale), but the hourly CHART read it as kWh per hour and so drew
       every import bar at exactly half: 105,698 kWh against the 211,394 the same file
       reports daily. Solar and export in that series already divide by nDays, so only
       the import was halved — which quietly made solar look twice as large against it.
       (user 2026-11-04: "this data can help to validate") */
    const hPerDay = hourlyAvg.map((v) => (nDays ? v / nDays : 0));
    const flags = [];
    /* Say which days were left out and why — a quietly shortened period is the kind of
       thing that surfaces later as "your numbers don't match my bill". */
    if (fullDays.length && partialDays.length) {
        flags.push({
            type: 'partial-day',
            label: `Part-day${partialDays.length > 1 ? 's' : ''} excluded (incomplete meter data): ` +
                partialDays.map(d => `${dayLabel(d.serial)} (${d.seen.size}/48 readings)`).join(', '),
        });
    }
    const spikes = dayList.filter(d => dMed > 0 && d.dayImport > 3 * dMed).map(d => d.label);
    const holidays = dayList.filter(d => dMed > 0 && d.dayImport < 0.25 * dMed).map(d => d.label);
    if (spikes.length) flags.push({ type: 'event-spike', label: `Event spikes (day load > 3× median): ${spikes.join(', ')}` });
    if (holidays.length) flags.push({ type: 'holiday', label: `Holiday outliers (< 25% median): ${holidays.join(', ')}` });
    if (mean(hAvg) > 0 && stdev(hAvg) < 0.2 * mean(hAvg)) flags.push({ type: 'flat-24-7', label: '24/7 flat load profile (hourly std < 20% of mean)' });
    if (stats.nightImportMedian > stats.dayImportMedian) flags.push({ type: 'night-heavy', label: 'Night-heavy load (night median > day median)' });

    // Chart aggregations. The `solar` candle is the SELF-USED solar (d.self) — the surplus
    // is drawn separately as export, so they don't double-count. `solarGen` = total
    // generation (for the "Solar generated" stat card). self + export = gen. (user 2026-07-25)
    const daily = dayList.map(d => ({
        label: d.label,
        dayImport: +d.dayImport.toFixed(1), nightImport: +d.nightImport.toFixed(1),
        solar: +d.self.toFixed(1), solarGen: +dailySolar.toFixed(1), export: +d.export.toFixed(1),
    }));
    const hourly = Array.from({ length: 24 }, (_, h) => {
        const dayImp = (hPerDay[h] || 0);   // kWh in this hour of a typical day
        const isDay = h >= DAY_START && h < DAY_END;
        const sol = (solarSlot[h * 2] || 0) + (solarSlot[h * 2 + 1] || 0);
        // avg export this hour across days
        let expSum = 0; for (const d of dayList) expSum += (Math.max(0, (solarSlot[h*2]||0) - d.slots[h*2]) + Math.max(0, (solarSlot[h*2+1]||0) - d.slots[h*2+1]));
        const avgExp = expSum / nDays;
        return {
            label: `${String(h).padStart(2, '0')}:00`,
            /* NULL, NOT ZERO, outside each window. The two are mutually exclusive, and a
               zero is a real reading that a line must travel to — which drew a diagonal
               across the 07:00 and 18:00 handover instead of the vertical edge the tariff
               boundary actually is. A null means "this series does not exist here", so the
               band simply closes at 90°. Consumers add them with `|| 0`, so totals are
               unchanged. (user 2026-11-04: "blue and purple shd be straight vertically
               90degree line right?") */
            dayImport: isDay ? +dayImp.toFixed(1) : null,
            nightImport: isDay ? null : +dayImp.toFixed(1),
            /* One continuous series for the hourly chart to draw. The day/night split is
               a TARIFF boundary, not a break in the meter: the site imports around the
               clock. Drawing two bands made the handover either a diagonal (zeros) or a
               hole (nulls); one band coloured by window gives a vertical colour change
               and no gap. The split keys above remain, for the tooltip and for every
               other consumer. (user 2026-11-04: "blank its weird") */
            gridImport: +dayImp.toFixed(1),
            isDayHour: isDay,
            solar: +Math.max(0, sol - avgExp).toFixed(1), solarGen: +sol.toFixed(1), export: +avgExp.toFixed(1),
        };
    });
    const monthMap = new Map();
    for (const d of dayList) {
        const mk = d.label.slice(0, 2);  // month (MM) from the MM-DD label
        if (!monthMap.has(mk)) monthMap.set(mk, { label: mk, dayImport: 0, nightImport: 0, solar: 0, solarGen: 0, export: 0 });
        const m = monthMap.get(mk);
        m.dayImport += d.dayImport; m.nightImport += d.nightImport; m.solar += d.self; m.solarGen += dailySolar; m.export += d.export;
    }
    const monthly = [...monthMap.values()].map(m => ({
        label: m.label, dayImport: +m.dayImport.toFixed(0), nightImport: +m.nightImport.toFixed(0),
        solar: +m.solar.toFixed(0), solarGen: +m.solarGen.toFixed(0), export: +m.export.toFixed(0),
    }));

    return { stats, flags, charts: { daily, hourly, monthly } };
}
